import { FC, useState } from 'react';
import { useStyles } from './styles';
import { PasswordUpdateDialogProps } from './types';
import { Button, Grid, Paper, Modal, TextField, InputAdornment, IconButton } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { observer } from 'mobx-react-lite';
import { useMutation } from 'react-query';
import axios from 'server';
import { useStore } from 'hooks/useRootStore';

export const PasswordUpdateDialog: FC<PasswordUpdateDialogProps> = observer(({ openModal, handleClose }) => {
  const classes = useStyles();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const {
    notificationStore: { openNotification },
  } = useStore();

  const updatePasswordMutation = useMutation(
    (data: { password: string; password_confirmation: string }) => axios.post('auth/password/update', data),
    {
      onSuccess: () => {
        openNotification('success', 'Password updated successfully');
        handleClose();
        setPassword('');
        setConfirmPassword('');
      },
      onError: (error: any) => {
        openNotification('error', error?.response?.data?.message || 'Failed to update password');
      },
    }
  );

  const handleSubmit = () => {
    if (!password || !confirmPassword) {
      openNotification('error', 'Please fill all fields');
      return;
    }
    if (password !== confirmPassword) {
      openNotification('error', 'Passwords do not match');
      return;
    }
    updatePasswordMutation.mutate({ password, password_confirmation: confirmPassword });
  };

  return (
    <Modal className={classes.modal} disablePortal disableEnforceFocus open={openModal} onEscapeKeyDown={handleClose}>
      <Paper className={classes.paper} elevation={3}>
        <p className={classes.title}>Update Password</p>
        <p className={classes.subtitle}>Please update your password to continue</p>

        <TextField
          fullWidth
          type={showPassword ? 'text' : 'password'}
          label='New Password'
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={classes.textField}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          type={showConfirmPassword ? 'text' : 'password'}
          label='Confirm Password'
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className={classes.textField}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Grid className={classes.marginTop} container justify='space-between'>
          <Button
            className={classes.actionBtn}
            variant='contained'
            color='primary'
            onClick={handleSubmit}
            disabled={updatePasswordMutation.isLoading}
          >
            {updatePasswordMutation.isLoading ? 'Updating...' : 'Update Password'}
          </Button>
          <Button className={classes.cancelButton} onClick={handleClose}>
            Cancel
          </Button>
        </Grid>
      </Paper>
    </Modal>
  );
});
